import type { QueryClient } from '@tanstack/react-query';
import {
  AuditQuestionCompletionStatus,
  ResponseResult,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import type { CacheSnapshot } from './patchAuditSummaryCache';
import { deriveParentCompletion, deriveParentResult } from './deriveParentResult';
import {
  EMPTY_RESPONSE,
  applyMetricsDiff,
  auditSummaryPredicate,
  buildNextCacheData,
} from './patchAuditCachePlumbing';

/**
 * Optimistic cache patches for ANSWER saves (flat, per-sub, bulk).
 * Field saves (comment / naReason / mediaIds) live in
 * patchAuditFieldCaches. All patches snapshot the whole summary
 * output for rollbackAuditQuestionResponseCache.
 */

export interface QuestionResponsePatch {
  readonly responseResult: ResponseResult;
  readonly respondedBy: string;
  /** Defaults to `new Date()` at patch time. */
  readonly respondedAt?: Date;
}

export interface SubQuestionResponsePatch {
  readonly subQuestionId: string;
  readonly result: ResponseResult;
  readonly respondedBy: string;
  readonly respondedAt?: Date;
  /** Child count from the full-question fetch — the summary cache
   *  only carries answered children. */
  readonly totalSubQuestions: number;
}

/**
 * Patch a flat question's answer: overwrite result, flip status to
 * COMPLETED, stamp responder, diff-update audit.metrics.
 */
export function snapshotAndPatchAuditQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionResponsePatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });

  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    if (!cachedData?.questions || !cachedData.audit) continue;
    const targetIndex = cachedData.questions.findIndex(
      (q) => q.questionDetails.questionId === questionId,
    );
    if (targetIndex < 0) continue;

    const previousResponse =
      cachedData.questions.at(targetIndex)?.responseDetails?.mainQuestionResponse;
    const previousResult = previousResponse?.responseResult;

    snapshots.push({ queryKey, data: cachedData });

    const nextResponse: QuestionResponseDetails = {
      ...(previousResponse ?? EMPTY_RESPONSE),
      responseStatus: AuditQuestionCompletionStatus.COMPLETED,
      responseResult: patch.responseResult,
      respondedBy: patch.respondedBy,
      respondedAt: now,
    };
    // Non-NA answers clear the stale reason label (cache/wire parity —
    // the picker visibility rule reads this slot).
    if (patch.responseResult !== ResponseResult.NA) {
      delete (nextResponse as Partial<QuestionResponseDetails>).naReason;
    }

    const nextQuestions = cachedData.questions.map((q, i) =>
      i === targetIndex
        ? {
            ...q,
            responseDetails: {
              ...q.responseDetails,
              mainQuestionResponse: nextResponse,
            },
          }
        : q,
    );

    const nextMetrics = applyMetricsDiff(
      cachedData.audit.metrics,
      previousResult,
      patch.responseResult,
    );
    queryClient.setQueryData(queryKey, {
      ...cachedData,
      audit: nextMetrics
        ? { ...cachedData.audit, metrics: nextMetrics }
        : cachedData.audit,
      questions: nextQuestions,
    });
  }

  return snapshots;
}

/**
 * Patch one sub-question's answer, then independently re-derive the
 * parent's aggregate result and all-children-answered completion.
 * FAIL may determine the aggregate before the parent is complete.
 */
export function snapshotAndPatchAuditSubQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: SubQuestionResponsePatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeSubQuestionResponsePatch(cachedData, questionId, patch, now);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeSubQuestionResponsePatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: SubQuestionResponsePatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  const nextSubResponses = mergeSubResponsesForCache(
    previousSubResponses,
    patch.subQuestionId,
    patch.result,
    patch.respondedBy,
    now,
  );

  const derivedNextMain = deriveParentResult(nextSubResponses, patch.totalSubQuestions);
  const previousCompleted = deriveParentCompletion(
    previousSubResponses,
    patch.totalSubQuestions,
  );
  const nextCompleted = deriveParentCompletion(nextSubResponses, patch.totalSubQuestions);

  // Status reflects structural completion; result follows audit
  // aggregation precedence and may already be FAIL while incomplete.
  const nextMainResponse = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: nextCompleted
      ? AuditQuestionCompletionStatus.COMPLETED
      : AuditQuestionCompletionStatus.IN_PROGRESS,
    responseResult: derivedNextMain,
    respondedBy: patch.respondedBy,
    respondedAt: now,
  } as unknown as QuestionResponseDetails;

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      derivedNextMain,
      previousCompleted,
      nextCompleted,
    ),
  );
}

/** Replace or append the target sub entry with the new answer. */
function mergeSubResponsesForCache(
  existing: readonly SubQuestionResponseEntry[],
  targetSubQuestionId: string,
  result: ResponseResult,
  respondedBy: string,
  respondedAt: Date,
): SubQuestionResponseEntry[] {
  const targetIndex = existing.findIndex(
    (entry) => entry.subQuestionId === targetSubQuestionId,
  );
  const previousTargetResponse =
    targetIndex >= 0 ? existing.at(targetIndex)?.response : undefined;

  const nextTargetResponse: QuestionResponseDetails = {
    ...(previousTargetResponse ?? EMPTY_RESPONSE),
    responseStatus: AuditQuestionCompletionStatus.COMPLETED,
    responseResult: result,
    respondedBy,
    respondedAt,
  };
  if (result !== ResponseResult.NA) {
    delete (nextTargetResponse as Partial<QuestionResponseDetails>).naReason;
  }

  if (targetIndex >= 0) {
    return existing.map((entry, i) =>
      i === targetIndex ? { ...entry, response: nextTargetResponse } : entry,
    );
  }
  return [
    ...existing,
    { subQuestionId: targetSubQuestionId, response: nextTargetResponse },
  ];
}

export interface BulkAnswerPatch {
  /** Definitive child id list from the full-question fetch. */
  readonly subQuestionIds: readonly string[];
  readonly result: ResponseResult;
  readonly respondedBy: string;
  readonly respondedAt?: Date;
}

/**
 * Patch a bulk Pass-All / Fail-All / NA-All: every child gets the
 * result, the parent takes it directly (uniform by construction).
 */
export function snapshotAndPatchAuditBulkAnswer(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: BulkAnswerPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeBulkAnswerPatch(cachedData, questionId, patch, now);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeBulkAnswerPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: BulkAnswerPatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousCompleted =
    previousMainResponse?.responseStatus === AuditQuestionCompletionStatus.COMPLETED;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  const nextMainResponse: QuestionResponseDetails = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: AuditQuestionCompletionStatus.COMPLETED,
    responseResult: patch.result,
    respondedBy: patch.respondedBy,
    respondedAt: now,
  };
  if (patch.result !== ResponseResult.NA) {
    delete (nextMainResponse as Partial<QuestionResponseDetails>).naReason;
  }
  const nextSubResponses = bulkOverwriteSubResponses(
    previousSubResponses,
    patch.subQuestionIds,
    patch.result,
    patch.respondedBy,
    now,
  );

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      patch.result,
      previousCompleted,
      true,
    ),
  );
}

/** Rebuild every child row with the bulk result, preserving prior metadata. */
function bulkOverwriteSubResponses(
  existing: readonly SubQuestionResponseEntry[],
  subQuestionIds: readonly string[],
  result: ResponseResult,
  respondedBy: string,
  respondedAt: Date,
): SubQuestionResponseEntry[] {
  const existingById = new Map(existing.map((entry) => [entry.subQuestionId, entry]));
  return subQuestionIds.map((subQuestionId) => {
    const prev = existingById.get(subQuestionId);
    const nextResponse: QuestionResponseDetails = {
      ...(prev?.response ?? EMPTY_RESPONSE),
      responseStatus: AuditQuestionCompletionStatus.COMPLETED,
      responseResult: result,
      respondedBy,
      respondedAt,
    };
    if (result !== ResponseResult.NA) {
      delete (nextResponse as Partial<QuestionResponseDetails>).naReason;
    }
    return { subQuestionId, response: nextResponse };
  });
}

/** Rollback shares the summary-snapshot shape — one function serves all patches. */
export { rollbackAuditSummaryCache as rollbackAuditQuestionResponseCache } from './patchAuditSummaryCache';
